import React, { useState, useMemo, useRef } from 'react';
import { get as _get } from 'lodash';
import { ProfileData } from '../../types';
import Button from '../common/Button';
import Icon from '../common/Icon';

interface OutreachTabProps {
  profile: ProfileData;
  onGenerateDraft: (
    channel: string,
    tone: string,
    objective: string,
    extraContext: string,
    painPoints: string[],
    whitepaperUrl: string
  ) => Promise<void>;
  isGenerating: boolean;
  draft: string;
  onCopy: (text: string) => void;
}

function isValidUrl(value: string): boolean {
  if (!value) return true; // empty is fine
  try {
    const u = new URL(value);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

/** Safely parse recommendedTone which may arrive as a JSON-encoded string or an array */
function parseToneOptions(raw: string | string[] | null | undefined): string[] {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.filter(Boolean);
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed.filter(Boolean);
  } catch {
    // treat as a plain comma-separated or single string
  }
  return raw.split(',').map((s) => s.trim()).filter(Boolean);
}

const FALLBACK_CHANNELS = ['Cold Email', 'LinkedIn DM', 'Follow-up'];
const FALLBACK_TONES = ['Friendly / Casual', 'Assertive / Warm', 'Direct / Data-led'];

const OutreachTab: React.FC<OutreachTabProps> = ({
  profile,
  onGenerateDraft,
  isGenerating,
  draft,
  onCopy,
}) => {
  const name = _get(profile, 'name') || '';

  // Derive channel & tone option lists from profile.outreach with fallbacks
  const channelOptions = useMemo<string[]>(() => {
    const raw = _get(profile, 'outreach.preferredChannels');
    if (Array.isArray(raw) && raw.length > 0) return raw.filter(Boolean);
    return FALLBACK_CHANNELS;
  }, [profile]);

  const toneOptions = useMemo<string[]>(() => {
    const raw = _get(profile, 'outreach.recommendedTone');
    const parsed = parseToneOptions(raw);
    return parsed.length > 0 ? parsed : FALLBACK_TONES;
  }, [profile]);

  const [channel, setChannel] = useState(() => channelOptions[0] || FALLBACK_CHANNELS[0]);
  const [tone, setTone] = useState(() => toneOptions[0] || FALLBACK_TONES[0]);
  const [objective, setObjective] = useState('Schedule discovery call');
  const [extraContext, setExtraContext] = useState('');

  // Pain Points state — seed from profile enrichment data if available
  const seedPainPoints = useMemo<string[]>(() => {
    const raw = (_get(profile, 'pain_points') || _get(profile, 'painPoints')) as unknown;
    if (Array.isArray(raw) && (raw as unknown[]).length > 0) return (raw as unknown[]).filter(Boolean) as string[];
    return [];
  }, [profile]);

  const [painPoints, setPainPoints] = useState<string[]>(() => seedPainPoints);
  const [painPointInput, setPainPointInput] = useState('');

  // Whitepaper / Content URL state
  const [whitepaperUrl, setWhitepaperUrl] = useState('');
  const urlValid = isValidUrl(whitepaperUrl);

  const painInputRef = useRef<HTMLInputElement>(null);

  const handleAddPainPoint = () => {
    const trimmed = painPointInput.trim();
    if (!trimmed) return;
    setPainPoints((prev) => [...prev, trimmed]);
    setPainPointInput('');
    painInputRef.current?.focus();
  };

  const handleRemovePainPoint = (idx: number) => {
    setPainPoints((prev) => prev.filter((_, i) => i !== idx));
  };

  const handlePainInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddPainPoint();
    }
  };

  const handleGenerate = () => {
    onGenerateDraft(channel, tone, objective, extraContext, painPoints, whitepaperUrl);
  };

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="bg-white border-2 border-brand-black rounded-brand p-10 shadow-lg">
        <div className="flex items-center justify-between mb-10">
          <div>
            <h2 className="text-2xl font-extrabold mb-1">Outreach Co-Pilot</h2>
            <p className="text-sm text-brand-grey08">
              Tailored messaging for {name || 'prospect'}
            </p>
          </div>
          {/*<span className="bg-brand-mustard text-brand-black px-4 py-1 rounded-pill text-[8px] font-black uppercase tracking-widest">
            Model: Gemini 3 Flash
          </span>*/}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="relative">
                <label className="block text-[10px] font-extrabold uppercase tracking-widest text-brand-black mb-2">
                  Channel
                </label>
                <select
                  value={channel}
                  onChange={(e) => setChannel(e.target.value)}
                  className="w-full bg-brand-white border border-brand-grey02 rounded-field p-3 text-xs font-bold outline-none focus:border-brand-pink transition-colors"
                >
                  {channelOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>

              <div className="relative">
                <label className="block text-[10px] font-extrabold uppercase tracking-widest text-brand-black mb-2">
                  Tone
                </label>
                <select
                  value={tone}
                  onChange={(e) => setTone(e.target.value)}
                  className="w-full bg-brand-white border border-brand-grey02 rounded-field p-3 text-xs font-bold outline-none focus:border-brand-pink transition-colors"
                >
                  {toneOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-[10px] font-extrabold uppercase mb-2">
                Campaign Objective
              </label>
              <input
                type="text"
                value={objective}
                onChange={(e) => setObjective(e.target.value)}
                className="w-full border border-brand-grey02 rounded-field p-3 text-xs outline-none focus:border-brand-pink transition-colors"
                placeholder="e.g. Discuss Q3 ROI projections"
              />
            </div>

            {/* ── Pain Points ─────────────────────────────────── */}
            <div>
              <label className="block text-[10px] font-extrabold uppercase mb-2">
                Pain Points
              </label>

              {/* Existing pain point chips */}
              {painPoints.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {painPoints.map((pt, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center gap-1.5 bg-brand-grey01 border border-brand-grey02 text-brand-black text-[11px] font-semibold px-3 py-1.5 rounded-full"
                    >
                      {pt}
                      <button
                        type="button"
                        onClick={() => handleRemovePainPoint(idx)}
                        className="text-brand-grey08 hover:text-brand-pink transition-colors leading-none"
                        aria-label={`Remove pain point: ${pt}`}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}

              {/* Add new pain point */}
              <div className="flex gap-2">
                <input
                  ref={painInputRef}
                  type="text"
                  value={painPointInput}
                  onChange={(e) => setPainPointInput(e.target.value)}
                  onKeyDown={handlePainInputKeyDown}
                  placeholder="Add a pain point..."
                  className="flex-1 border border-brand-grey02 rounded-field p-3 text-xs outline-none focus:border-brand-pink transition-colors"
                />
                <button
                  type="button"
                  onClick={handleAddPainPoint}
                  disabled={!painPointInput.trim()}
                  className="px-4 py-2 text-xs font-bold border border-brand-grey02 rounded-field bg-brand-white hover:bg-brand-grey01 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Add
                </button>
              </div>
              <p className="text-[10px] text-brand-grey08 mt-1.5">
                Pain points are auto-extracted from enrichment data. Add or remove as needed.
              </p>
            </div>

            {/* ── Whitepaper / Content URL ─────────────────────── */}
            <div>
              <label className="block text-[10px] font-extrabold uppercase mb-2">
                Whitepaper/Content URL <span className="normal-case font-medium">(optional)</span>
              </label>
              <input
                type="url"
                value={whitepaperUrl}
                onChange={(e) => setWhitepaperUrl(e.target.value)}
                placeholder="e.g., https://example.com/whitepaper"
                className={`w-full border rounded-field p-3 text-xs outline-none transition-colors ${
                  !urlValid
                    ? 'border-red-400 focus:border-red-500'
                    : 'border-brand-grey02 focus:border-brand-pink'
                }`}
              />
              {!urlValid && (
                <p className="text-[10px] text-red-500 mt-1">
                  Please enter a valid URL (e.g. https://example.com/whitepaper)
                </p>
              )}
              {urlValid && (
                <p className="text-[10px] text-brand-grey08 mt-1.5">
                  Add URL of content they recently consumed to personalise outreach
                </p>
              )}
            </div>

            {/* ── Additional Context ───────────────────────────── */}
            <div>
              <label className="block text-[10px] font-extrabold uppercase mb-2">
                Additional Context (Optional)
              </label>
              <textarea
                value={extraContext}
                onChange={(e) => setExtraContext(e.target.value)}
                className="w-full border border-brand-grey02 rounded-field p-3 text-xs outline-none focus:border-brand-pink transition-colors h-24 resize-none"
                placeholder="Add specific details like shared acquaintances, recent news, or unique hooks..."
              />
            </div>

            <Button
              variant="primary"
              size="lg"
              icon="edit_note"
              onClick={handleGenerate}
              isLoading={isGenerating}
              className="w-full shadow-xl"
            >
              {isGenerating ? 'Thinking...' : 'Generate Draft'}
            </Button>
          </div>

          <div className="bg-brand-grey01 rounded-brand p-8 border border-brand-grey02 relative group min-h-[400px]">
            <div className="flex justify-between items-center mb-6">
              <span className="text-[10px] font-extrabold uppercase tracking-widest text-brand-grey08">
                Draft workbench
              </span>
              {draft && (
                <button
                  onClick={() => onCopy(draft)}
                  className="text-brand-pink text-[10px] font-bold hover:underline flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <Icon name="content_copy" size="sm" />
                  COPY
                </button>
              )}
            </div>
            <div className="text-sm font-medium leading-relaxed whitespace-pre-wrap text-brand-grey09 font-mono">
              {draft || 'Select your channel and objective to start drafting...'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OutreachTab;
